import { showProviderCreditPrompt } from '../../lib/providerCredits';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { streamRun } from '../../lib/runStream';
import { showUpgradePrompt } from '../plans/UpgradePrompt';
import type { Block, RunEvent } from '../../types/api';

export interface ToolState { name: string; activity: string; executor: 'client' | 'server'; status: 'running' | 'done' | 'error'; summary?: string; input?: any }
export interface ArtifactRef { artifactId: string; versionId: string; versionNumber: number; title: string; artifactType: string; action: 'created' | 'updated' }

export interface LiveTurn {
  runId: string;
  assistantMessageId: string;
  userMessageId: string;
  mode: string;
  blocks: Record<number, Block>;
  tools: Record<string, ToolState>;
  artifacts: ArtifactRef[];
  status: string;
  statusDetail?: string;
  done: boolean;
  error?: string;
  usage?: any;
}

export function applyEvent(t: LiveTurn, e: RunEvent): LiveTurn {
  switch (e.type) {
    case 'run.status': return { ...t, status: e.status, statusDetail: e.detail };
    case 'block.start': return { ...t, blocks: { ...t.blocks, [e.index]: { ...e.block } } };
    case 'block.delta': {
      const b = { ...(t.blocks[e.index] ?? { type: 'text', text: '' }) };
      if (e.delta.kind === 'text') b.text = (b.text ?? '') + (e.delta.text ?? '');
      else if (e.delta.kind === 'thinking') b.thinking = (b.thinking ?? '') + (e.delta.text ?? '');
      else if (e.delta.kind === 'input_json') b._json = (b._json ?? '') + (e.delta.text ?? '');
      else if (e.delta.kind === 'citation') b.citations = [...(b.citations ?? []), e.delta.citation];
      return { ...t, blocks: { ...t.blocks, [e.index]: b } };
    }
    case 'block.stop': return { ...t, blocks: { ...t.blocks, [e.index]: e.block } };
    case 'tool.start': return { ...t, tools: { ...t.tools, [e.toolUseId]: { name: e.name, activity: e.activity, executor: e.executor, status: 'running', input: e.input } } };
    case 'tool.result': {
      const prev = t.tools[e.toolUseId] ?? { name: e.name, activity: e.name, executor: 'server' as const, status: 'running' as const };
      return { ...t, tools: { ...t.tools, [e.toolUseId]: { ...prev, status: e.isError ? 'error' : 'done', summary: e.summary } } };
    }
    case 'artifact.updated': return { ...t, artifacts: [...t.artifacts.filter((a) => !(a.artifactId === e.artifactId && a.versionId === e.versionId)), e] };
    case 'usage': return { ...t, usage: e.usage };
    case 'run.done': return { ...t, done: true, status: e.status, error: e.error };
    default: return t;
  }
}

/** Tracks the in-flight run for a conversation and reattaches to it after navigation or reload. */
export function useRun(conversationId: string | undefined, opts: { onArtifact?: (a: ArtifactRef) => void } = {}) {
  const qc = useQueryClient();
  const [live, setLive] = useState<LiveTurn | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const onArtifact = useRef(opts.onArtifact);
  onArtifact.current = opts.onArtifact;

  const currentRun = useRef<string | null>(null);
  const attach = useCallback((runId: string, ids?: { assistantMessageId: string; userMessageId: string }) => {
    // Already following this run: keep the existing stream instead of replaying it twice.
    if (currentRun.current === runId && abortRef.current && !abortRef.current.signal.aborted) return;
    currentRun.current = runId;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLive({ runId, assistantMessageId: ids?.assistantMessageId ?? '', userMessageId: ids?.userMessageId ?? '', mode: 'chat', blocks: {}, tools: {}, artifacts: [], status: 'queued', done: false });
    void streamRun(runId, (e) => {
      setLive((t) => {
        if (!t || t.runId !== runId) return t;
        if (e.type === 'run.started') return { ...t, assistantMessageId: e.assistantMessageId, userMessageId: e.userMessageId, mode: e.mode };
        return applyEvent(t, e);
      });
      if(e.type==='provider.credit_exhausted')showProviderCreditPrompt(e,`run:${runId}`);
      if (e.type === 'usage' || e.type === 'run.done') void qc.invalidateQueries({ queryKey: ['account'] });
      if (e.type === 'run.done' && e.error && !/API credits are exhausted/i.test(e.error) && /credit|5.hour|weekly.*limit/i.test(e.error)) {
        showUpgradePrompt();
      }
      if (e.type === 'run.started' && conversationId) void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      if (e.type === 'artifact.updated') {
        void qc.invalidateQueries({ queryKey: ['artifact', e.artifactId] }); void qc.invalidateQueries({ queryKey: ['artifact-summary', e.artifactId] });
        void qc.invalidateQueries({ queryKey: ['artifacts'] });
        onArtifact.current?.(e);
      }
      if (e.type === 'media.updated') { void qc.invalidateQueries({ queryKey: ['media'] }); void qc.invalidateQueries({ queryKey: ['media-item', e.mediaId] }); }
      if (e.type === 'run.done') {
        void (async () => {
          if (conversationId) await qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
          // Several tools may have touched artifacts in quick succession; refresh them once the turn ends.
          void qc.invalidateQueries({ queryKey: ['artifact'] }); void qc.invalidateQueries({ queryKey: ['artifact-summary'] });
          setLive((t) => (t && t.runId === runId ? null : t));
          if (currentRun.current === runId) currentRun.current = null;
          void qc.invalidateQueries({ queryKey: ['conversations'] });
          // Titles are generated right after the reply finishes.
          setTimeout(() => void qc.invalidateQueries({ queryKey: ['conversations'] }), 2500);
        })();
      }
    }, ac.signal);
  }, [conversationId, qc]);

  useEffect(() => () => abortRef.current?.abort(), [conversationId]);

  const cancel = useCallback(async () => {
    if (live && !live.done) await api(`/api/runs/${live.runId}/cancel`, { method: 'POST', body: {} });
  }, [live]);

  return { live, attach, cancel, setLive };
}
